import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { format, differenceInDays } from 'date-fns';
import { Check, ArrowLeft } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { UpsellCard } from '../components/UpsellCard';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const Booking = () => {
  const { roomId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { t, language } = useLanguage();

  const [room, setRoom] = useState(null);
  const [upsells, setUpsells] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [step, setStep] = useState(1); // 1: upsells, 2: details

  const checkIn = searchParams.get('checkIn') ? new Date(searchParams.get('checkIn')) : null;
  const checkOut = searchParams.get('checkOut') ? new Date(searchParams.get('checkOut')) : null;
  const guests = parseInt(searchParams.get('guests') || '2');

  const [selectedUpsells, setSelectedUpsells] = useState([]);
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
    specialRequests: '',
  });

  const nights = checkIn && checkOut ? differenceInDays(checkOut, checkIn) : 0;

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [roomRes, upsellsRes] = await Promise.all([
          axios.get(`${API}/rooms/${roomId}`),
          axios.get(`${API}/upsells`),
        ]);
        setRoom(roomRes.data);
        setUpsells(upsellsRes.data);
      } catch (error) {
        console.error('Error fetching data:', error);
        toast.error('Failed to load booking data');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [roomId]);

  const roomPrice = room ? room.price_per_night * nights : 0;
  const upsellsPrice = selectedUpsells.reduce((sum, id) => {
    const upsell = upsells.find((u) => u.id === id);
    return sum + (upsell?.price || 0);
  }, 0);
  const totalPrice = roomPrice + upsellsPrice;

  const toggleUpsell = (id) => {
    setSelectedUpsells((prev) =>
      prev.includes(id) ? prev.filter((u) => u !== id) : [...prev, id]
    );
  };

  const handleSubmit = async () => {
    if (!formData.fullName || !formData.email || !formData.phone) {
      toast.error('Please fill in all required fields');
      return;
    }

    setSubmitting(true);
    try {
      // Create booking
      const bookingRes = await axios.post(`${API}/bookings`, {
        room_id: roomId,
        guest_name: formData.fullName,
        guest_email: formData.email,
        guest_phone: formData.phone,
        check_in: checkIn.toISOString(),
        check_out: checkOut.toISOString(),
        guests: guests,
        upsell_ids: selectedUpsells,
        special_requests: formData.specialRequests,
        language: language,
      });

      const bookingId = bookingRes.data.id;

      // Create checkout session
      const checkoutRes = await axios.post(`${API}/payments/checkout`, {
        booking_id: bookingId,
        origin_url: window.location.origin,
      });

      // Redirect to Stripe
      if (checkoutRes.data.url) {
        window.location.href = checkoutRes.data.url;
      } else {
        throw new Error('No checkout URL received');
      }
    } catch (error) {
      console.error('Booking error:', error);
      const errorMessage = error.response?.data?.detail || 'Failed to create booking. Please try again.';
      toast.error(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F9F8F6] pt-20 flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-2 border-[#D4AF37] border-t-transparent rounded-full" />
      </div>
    );
  }

  if (!room || !checkIn || !checkOut) {
    return (
      <div className="min-h-screen bg-[#F9F8F6] pt-20 flex items-center justify-center">
        <p className="text-[#64748B]">Invalid booking details</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F9F8F6] pt-24 pb-16" data-testid="booking-page">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        {/* Back Button */}
        <button
          onClick={() => navigate(-1)}
          className="flex items-center space-x-2 text-[#64748B] hover:text-[#0F1F3C] mb-8 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          <span className="text-sm font-sans">{t('common.back')}</span>
        </button>

        {/* Progress */}
        <div className="flex items-center justify-center mb-12">
          <div className="flex items-center space-x-4">
            <div
              className={`w-8 h-8 rounded-full flex items-center justify-center ${
                step >= 1 ? 'bg-[#D4AF37] text-white' : 'bg-gray-200 text-gray-500'
              }`}
            >
              {step > 1 ? <Check className="w-4 h-4" /> : '1'}
            </div>
            <div className={`w-24 h-0.5 ${step > 1 ? 'bg-[#D4AF37]' : 'bg-gray-200'}`} />
            <div
              className={`w-8 h-8 rounded-full flex items-center justify-center ${
                step >= 2 ? 'bg-[#D4AF37] text-white' : 'bg-gray-200 text-gray-500'
              }`}
            >
              2
            </div>
          </div>
        </div>

        <div className="grid lg:grid-cols-3 gap-12">
          {/* Main Content */}
          <div className="lg:col-span-2">
            {step === 1 && (
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.4 }}
              >
                <h2 className="font-serif text-3xl text-[#0F1F3C] mb-2">{t('booking.enhanceStay')}</h2>
                <p className="text-[#64748B] font-sans mb-8">{t('experiences.subtitle')}</p>

                {/* Spa */}
                <div className="mb-10">
                  <h3 className="text-xs uppercase tracking-[0.2em] text-[#D4AF37] font-sans font-medium mb-6">
                    {t('experiences.spa')}
                  </h3>
                  <div className="grid md:grid-cols-2 gap-6">
                    {upsells
                      .filter((u) => u.category === 'spa')
                      .map((upsell, index) => (
                        <UpsellCard
                          key={upsell.id}
                          upsell={upsell}
                          index={index}
                          isSelected={selectedUpsells.includes(upsell.id)}
                          onToggle={() => toggleUpsell(upsell.id)}
                        />
                      ))}
                  </div>
                </div>

                {/* Shuttle */}
                <div className="mb-10">
                  <h3 className="text-xs uppercase tracking-[0.2em] text-[#D4AF37] font-sans font-medium mb-6">
                    {t('experiences.shuttle')}
                  </h3>
                  <div className="grid md:grid-cols-2 gap-6">
                    {upsells
                      .filter((u) => u.category === 'shuttle')
                      .map((upsell, index) => (
                        <UpsellCard
                          key={upsell.id}
                          upsell={upsell}
                          index={index}
                          isSelected={selectedUpsells.includes(upsell.id)}
                          onToggle={() => toggleUpsell(upsell.id)}
                        />
                      ))}
                  </div>
                </div>

                {/* Excursions */}
                <div className="mb-10">
                  <h3 className="text-xs uppercase tracking-[0.2em] text-[#D4AF37] font-sans font-medium mb-6">
                    {t('experiences.excursion')}
                  </h3>
                  <div className="grid md:grid-cols-2 gap-6">
                    {upsells
                      .filter((u) => u.category === 'excursion')
                      .map((upsell, index) => (
                        <UpsellCard
                          key={upsell.id}
                          upsell={upsell}
                          index={index}
                          isSelected={selectedUpsells.includes(upsell.id)}
                          onToggle={() => toggleUpsell(upsell.id)}
                        />
                      ))}
                  </div>
                </div>

                <button
                  onClick={() => setStep(2)}
                  className="w-full bg-[#0F1F3C] text-white hover:bg-[#1A2F4F] py-4 text-xs uppercase tracking-widest font-sans font-medium transition-all duration-300"
                  data-testid="continue-to-details-btn"
                >
                  {t('booking.continueToDetails')}
                </button>
              </motion.div>
            )}

            {step === 2 && (
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.4 }}
              >
                <h2 className="font-serif text-3xl text-[#0F1F3C] mb-8">{t('booking.guestDetails')}</h2>

                <div className="space-y-6">
                  <div>
                    <label className="text-xs uppercase tracking-[0.15em] text-[#0F1F3C]/60 font-sans font-medium mb-2 block">
                      {t('booking.fullName')} *
                    </label>
                    <Input
                      value={formData.fullName}
                      onChange={(e) => setFormData({ ...formData, fullName: e.target.value })}
                      className="border-gray-200 rounded-none focus:border-[#0F1F3C] focus:ring-0"
                      data-testid="guest-name-input"
                    />
                  </div>

                  <div>
                    <label className="text-xs uppercase tracking-[0.15em] text-[#0F1F3C]/60 font-sans font-medium mb-2 block">
                      {t('booking.email')} *
                    </label>
                    <Input
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      className="border-gray-200 rounded-none focus:border-[#0F1F3C] focus:ring-0"
                      data-testid="guest-email-input"
                    />
                  </div>

                  <div>
                    <label className="text-xs uppercase tracking-[0.15em] text-[#0F1F3C]/60 font-sans font-medium mb-2 block">
                      {t('booking.phone')} *
                    </label>
                    <Input
                      type="tel"
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      className="border-gray-200 rounded-none focus:border-[#0F1F3C] focus:ring-0"
                      data-testid="guest-phone-input"
                    />
                  </div>

                  <div>
                    <label className="text-xs uppercase tracking-[0.15em] text-[#0F1F3C]/60 font-sans font-medium mb-2 block">
                      {t('booking.specialRequests')} ({t('booking.optional')})
                    </label>
                    <Textarea
                      value={formData.specialRequests}
                      onChange={(e) => setFormData({ ...formData, specialRequests: e.target.value })}
                      className="border-gray-200 rounded-none focus:border-[#0F1F3C] focus:ring-0 min-h-[100px]"
                      data-testid="special-requests-input"
                    />
                  </div>
                </div>

                <div className="flex space-x-4 mt-8">
                  <button
                    onClick={() => setStep(1)}
                    className="flex-1 border border-[#0F1F3C] text-[#0F1F3C] hover:bg-[#0F1F3C] hover:text-white py-4 text-xs uppercase tracking-widest font-sans font-medium transition-all duration-300"
                  >
                    {t('common.back')}
                  </button>
                  <button
                    onClick={handleSubmit}
                    disabled={submitting}
                    className="flex-1 bg-[#D4AF37] text-white hover:bg-[#B5952F] py-4 text-xs uppercase tracking-widest font-sans font-medium transition-all duration-300 disabled:opacity-50"
                    data-testid="proceed-to-payment-btn"
                  >
                    {submitting ? t('common.loading') : t('booking.proceedToPayment')}
                  </button>
                </div>
              </motion.div>
            )}
          </div>

          {/* Summary Sidebar */}
          <div className="lg:col-span-1">
            <div className="bg-white p-6 shadow-[0_20px_40px_-15px_rgba(15,31,60,0.1)] sticky top-28">
              <h3 className="font-serif text-xl text-[#0F1F3C] mb-6">{t('booking.yourStay')}</h3>

              {/* Room */}
              <div className="flex space-x-4 pb-6 border-b border-gray-100">
                <img
                  src={room.images[0]}
                  alt={room.name[language] || room.name.en}
                  className="w-24 h-24 object-cover"
                />
                <div>
                  <h4 className="font-serif text-lg text-[#0F1F3C]">
                    {room.name[language] || room.name.en}
                  </h4>
                  <p className="text-sm text-[#64748B] font-sans">{room.view}</p>
                </div>
              </div>

              {/* Dates */}
              <div className="py-6 border-b border-gray-100 space-y-3">
                <div className="flex justify-between">
                  <span className="text-sm text-[#64748B] font-sans">{t('booking.checkIn')}</span>
                  <span className="text-sm text-[#0F1F3C] font-sans">{format(checkIn, 'MMM dd, yyyy')}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-[#64748B] font-sans">{t('booking.checkOut')}</span>
                  <span className="text-sm text-[#0F1F3C] font-sans">{format(checkOut, 'MMM dd, yyyy')}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-[#64748B] font-sans">{t('booking.guests')}</span>
                  <span className="text-sm text-[#0F1F3C] font-sans">{guests}</span>
                </div>
              </div>

              {/* Price Breakdown */}
              <div className="py-6 space-y-3">
                <div className="flex justify-between">
                  <span className="text-sm text-[#64748B] font-sans">
                    {t('booking.roomPrice')} ({nights} {nights === 1 ? t('booking.night') : t('booking.nights')})
                  </span>
                  <span className="text-sm text-[#0F1F3C] font-sans">€{roomPrice.toFixed(2)}</span>
                </div>
                {upsellsPrice > 0 && (
                  <div className="flex justify-between">
                    <span className="text-sm text-[#64748B] font-sans">{t('booking.upsellsPrice')}</span>
                    <span className="text-sm text-[#0F1F3C] font-sans">€{upsellsPrice.toFixed(2)}</span>
                  </div>
                )}
                {selectedUpsells.length > 0 && (
                  <div className="pt-2">
                    {selectedUpsells.map((id) => {
                      const upsell = upsells.find((u) => u.id === id);
                      return (
                        <div key={id} className="flex justify-between text-xs py-1">
                          <span className="text-[#64748B] font-sans">
                            • {upsell?.name[language] || upsell?.name.en}
                          </span>
                          <span className="text-[#0F1F3C] font-sans">€{upsell?.price}</span>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Total */}
              <div className="pt-6 border-t border-gray-100">
                <div className="flex justify-between items-center">
                  <span className="font-sans font-medium text-[#0F1F3C]">{t('booking.totalPrice')}</span>
                  <span className="font-serif text-2xl text-[#0F1F3C]">€{totalPrice.toFixed(2)}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
